/**
 * PWA-4 — durable queue storage on IndexedDB.
 *
 * THIS FILE IS THE STAGE-2 SWAP POINT. Everything else in `syncQueue/` is
 * platform-agnostic; a Capacitor build replaces this one module with
 * `@capacitor/preferences` (records) + `@capacitor/filesystem` (photo bytes) and
 * the queue logic, executors and UI carry over untouched.
 *
 * IndexedDB rather than localStorage because it stores Blobs natively — a
 * proof-of-work photo is megabytes, and base64 in localStorage would blow the
 * ~5 MB origin quota after two photos.
 *
 * Hand-rolled rather than pulling in `idb`: the surface is four methods on one
 * object store, and the wrapper is smaller than the dependency.
 *
 * ⚠️ Browser caveat that Stage 2 fixes: IndexedDB is evictable. iOS clears it
 * after ~7 days of no use, and any browser may evict under storage pressure.
 * `navigator.storage.persist()` is requested below to reduce the risk, but only
 * the native backend makes the queue genuinely durable.
 */

import type { QueuedAction, SyncQueueStorage } from './types';

const DB_NAME = 'rm24-sync';
const DB_VERSION = 1;
const STORE = 'actions';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, mode);
    const out = await fn(tx.objectStore(STORE));
    // Let the transaction settle before closing, or a write can be lost.
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    });
    return out;
  } finally {
    db.close();
  }
}

/** True when this browser can back the queue durably. */
export function isIdbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

/**
 * Ask the browser not to evict our storage. Best-effort and silently ignored
 * where unsupported; granted automatically on installed PWAs in Chrome, and
 * never on iOS Safari — the honest reason Stage 2's native backend exists.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export function createIdbStorage(): SyncQueueStorage {
  return {
    async list() {
      return withStore('readonly', (store) => promisify(store.getAll() as IDBRequest<QueuedAction[]>));
    },
    async put(action) {
      await withStore('readwrite', (store) => promisify(store.put(action)));
    },
    async remove(id) {
      await withStore('readwrite', (store) => promisify(store.delete(id)));
    },
    async clear() {
      await withStore('readwrite', (store) => promisify(store.clear()));
    },
  };
}
